import { useState } from 'react';
import { Calendar } from '@sara/ui/components/calendar';
import type { FilterEditorProps } from '../types';
import type { InterviewsSearch } from '../../../../../router/tree';

/**
 * Extract the DateRange type from Calendar's onSelect signature so we don't need to
 * directly depend on react-day-picker from the console app.
 */
type CalendarRangeProps = Extract<React.ComponentProps<typeof Calendar>, { mode: 'range' }>;
type DateRange = NonNullable<CalendarRangeProps['selected']>;

interface DateRangeEditorProps extends FilterEditorProps {
  afterParam: keyof InterviewsSearch;
  beforeParam: keyof InterviewsSearch;
}

function startOfDayIso(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfDayIso(date: Date): string {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

export function DateRangeEditor({
  search,
  onChange,
  afterParam,
  beforeParam,
}: DateRangeEditorProps) {
  const afterVal = search[afterParam] as string | undefined;
  const beforeVal = search[beforeParam] as string | undefined;

  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = afterVal ? new Date(afterVal) : undefined;
    const to = beforeVal ? new Date(beforeVal) : undefined;
    if (!from && !to) return undefined;
    return { from, to };
  });

  function handleSelect(next: DateRange | undefined) {
    setRange(next);
    onChange({
      [afterParam]: next?.from ? startOfDayIso(next.from) : undefined,
      [beforeParam]: next?.to ? endOfDayIso(next.to) : undefined,
    } as Partial<InterviewsSearch>);
  }

  return <Calendar mode="range" selected={range} onSelect={handleSelect} numberOfMonths={2} />;
}
